import { Autocomplete, Button, Group } from '@mantine/core';
import { useRef } from 'react';

type FoodAutocompleteInputProps = {
  /** Current text in the input (controlled by the parent). */
  value: string;
  /** Called as the user types or picks a suggestion. */
  onChange: (value: string) => void;
  /**
   * Commit a value to the underlying list. Receives the text to add (the typed
   * text on Enter/Add, or the selected suggestion). The parent owns parsing,
   * de-duplication and clearing the input afterwards.
   */
  onAdd: (text: string) => void;
  /** Suggestions shown in the dropdown (Mantine filters them by the input). */
  suggestions: string[];
  placeholder?: string;
  /** Button + accent colour, matching the surrounding section. */
  color?: string;
  buttonLabel?: string;
};

/**
 * A text input backed by ingredient/protein autocomplete for the freeform
 * "add a chip" preference fields (allergies, foods to avoid, other proteins).
 *
 * Selecting a suggestion adds it immediately; typing free text and pressing
 * Enter or clicking Add commits whatever was typed (arbitrary values are always
 * allowed — the suggestions only assist). The Enter handler is deferred a tick
 * so that when Enter is used to accept a highlighted suggestion, the option
 * submit wins and we don't also add the partial typed text.
 */
export const FoodAutocompleteInput = ({
  value,
  onChange,
  onAdd,
  suggestions,
  placeholder,
  color = 'red',
  buttonLabel = 'Add',
}: FoodAutocompleteInputProps) => {
  // Timestamp of the last option submit. Used to tell whether an Enter press
  // just accepted a highlighted suggestion (handled by onOptionSubmit) so we
  // don't also add the partially-typed free text. A timestamp — rather than a
  // boolean flag — is robust to handler ordering within the keypress and never
  // goes stale after an earlier mouse-click selection.
  const lastOptionSubmitRef = useRef(0);

  const commit = (text: string) => {
    if (!text.trim()) return;
    onAdd(text);
  };

  return (
    <Group gap="xs">
      <Autocomplete
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onOptionSubmit={(optionValue) => {
          lastOptionSubmitRef.current = Date.now();
          commit(optionValue);
          // Mantine sets the input to the selected option's label right after
          // this handler, which would leave stale text behind. Clear on the next
          // tick so it wins and the field is empty for the next entry.
          setTimeout(() => onChange(''), 0);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          // Defer so an option submit fired by the same Enter press is recorded
          // first; if one just happened, it already added the value.
          setTimeout(() => {
            if (Date.now() - lastOptionSubmitRef.current < 150) return;
            commit(value);
          }, 0);
        }}
        data={suggestions}
        style={{ flex: 1 }}
        size="sm"
        limit={8}
        comboboxProps={{ withinPortal: true }}
      />
      <Button size="sm" onClick={() => commit(value)} disabled={!value.trim()} color={color}>
        {buttonLabel}
      </Button>
    </Group>
  );
};
